import { useContext, useEffect, useMemo, useState } from "react";

import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import NoData from "../../../../../../images/NoData.svg";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import Close from "../../../../../../images/Close.svg";
import { useLazyQuery } from "@apollo/client";
import {
  LicenseTypes,
  Operation,
  PageFor,
  TableHeaders,
  TimeZone,
} from "../../../../../../utils/Enum.types";
import useAssetTableJson from "../../../../../../json/useAssetTableJson";
import useUrlParams from "../../../../../../customHooks/useUrlParams";

import {
  AtsStckItemDetailsEdges,
  AtsStckItemDetailsOrderField,
  filterDataType,
  GetItemsDetailsResult,
  GetItemsDetailsVars,
  ItemDetailsQueryType,
} from "../../items/queries/types";
import { GetItemsDetails, JunctionsNode } from "../../items/queries/queries";
import {
  DEFAULT_TIME,
  EMPTY_STRING,
  emptyFunc,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../../utils/constants";
import {
  nodeVar,
  OrderDirection,
} from "../../../../../../utils/commonQueryTypes";
import { TableHeaderProps } from "../../../../../../utils/types";
import { ColumnVisibilityFor } from "../../../../../../utils/Enum.Datagrid";
import {
  convertToTimeZone,
  CustomModal,
  getModifiedScrollHeight,
  handleMUISelectEvent,
  toStandardDate,
} from "../../../../../../utils/UtilFunctions";
import { AtsSubsidiary } from "../queries/types";
import { GetSubsidiaryNodeById } from "../queries/queries";
import useLabels from "../../../../../../customHooks/useLabels";
import Navbar from "../../../../../common/layout/Navbar";
import { Title } from "../../../../../../stories/Title/Title";
import Input from "../../../../../../stories/Input/Input";
import ItemGroupSelect from "../../../../../../customComponents/ItemGroupSelect";
import ItemCategorySelect from "../../../../../../customComponents/ItemCategorySelect";
import { StyledDatagrid } from "../../../../../../styles/DatagridStyles";
import {
  HEADER_TEXT_ALIGN,
  MARKS_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../../styles/Variables";
import { Button } from "../../../../../../stories/Button/Button";
import ImportItems from "../../items/ImportItems";
import { AddModalStyles } from "../../../../../../styles/CustomModalStyles";
import ImportItemsDetails from "../../../item/ImportItemsDetails";
import Edit from "../../../../../../images/EditProfile.svg";
import { modalOpertaionFlag } from "../../../../customer/Index";
import AddUsers from "../../../users/AddUser";
import AddItemDetails from "./itemdetails/AddItemDetails";
import { payloadTypes } from "../../../../../../context/reducer";
import { AppContext } from "../../../../../../context/context";
import { getSubOrSiteOrJunctionByIdResult, Junction } from "./queries/types";
import { GetSubOrSiteOrJunctionById } from "./queries/queries";
import useLoggedInUserDetails from "../../../../../../customHooks/useLoggedInUserDetails";
import PoleNumComponent from "../../../../../common/Autocomplete/PoleNumComponent";

interface Props extends NavTypeProps {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  pageType: PageFor;
}

export interface NavTypeProps {
  NavType: LicenseTypes;
}

const JunctionsItemList = ({ NavType, setModalFlag, pageType }: Props) => {
  const { Items } = useAssetTableJson();
  const { dispatch } = useContext(AppContext);

  const { customer_id, sub_id, company_id, siteID, junctionID } =
    useUrlParams();
  const { user_details } = useLoggedInUserDetails();
  const navigate = useNavigate();
  const [items, setItems] = useState<AtsStckItemDetailsEdges[]>([]);
  const [importModal, setImportModal] = useState(false);
  const [poleNum, setPoleNum] = useState<number | null>(null);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const [searchJunction, setSearchJunction] = useState("");
  const [filterData, setFilterData] = useState<filterDataType>({
    ItemGroupId: 0,
    ItemCategoryId: 0,
    UoMId: 0,
  });
  const [addModal, setAddModal] = useState<modalOpertaionFlag>({
    operation: Operation.CREATE,
    flag: false,
  });

  const [getJunctionByNode, { data: junctionData }] = useLazyQuery<
    { node: Junction },
    nodeVar
  >(JunctionsNode);

  const [GetJunctionItems, { data, loading, fetchMore }] = useLazyQuery<
    GetItemsDetailsResult,
    GetItemsDetailsVars
  >(GetItemsDetails);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id || company_id) {
          GetJunctionItems({
            variables: {
              customerID: customer_id ? customer_id : 0,
              companyID: company_id ? company_id : 0,
              subsidiaryID: sub_id ? sub_id : 0,
              siteID: siteID ? siteID : 0,
              junctionID: junctionID ? junctionID : 0,
              input: {
                query_type: ItemDetailsQueryType.ALL,
                stckCategoryID: filterData.ItemCategoryId
                  ? filterData.ItemCategoryId
                  : 0,
                stckGroupID: filterData.ItemGroupId
                  ? filterData.ItemGroupId
                  : 0,
                stckManufacturerID: 0,
                stckUomID: filterData.UoMId ? filterData.UoMId : 0,
                stckSubCategoryID: 0,
                itemID: 0,
                ldgrID: 0,
                poleNum,
                empID: user_details.id!,
              },
              after: null,
              first: ROWS_PER_PAGE,
              name: searchJunction,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckItemDetailsOrderField.CREATED_AT,
              },
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    sub_id,
    user_details.id,
    GetJunctionItems,
    searchJunction,
    filterData,
    poleNum,
  ]);

  useEffect(() => {
    if (junctionID) {
      getJunctionByNode({
        variables: {
          id: junctionID,
        },
      });
    }
  }, [getJunctionByNode, junctionID]);

  const dynamicHeaders: TableHeaderProps[] = Items.ItemDetailsList.map(
    (header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      cellClassName: header.cellClassName,
    })
  );
  const columns: GridColDef[] = [
    ...dynamicHeaders,
    {
      field: "actions",
      cellClassName: "td-actions-print ",
      headerClassName: "td-actions-print-header",
      headerAlign: HEADER_TEXT_ALIGN,
      align: MARKS_TEXT_ALIGN,
      headerName: TableHeaders.ACTION,
      renderCell: (params) => {
        return (
          <>
            <img
              src={Edit}
              alt="/"
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_ITEM_DETAILS_ID,
                  payload: {
                    itemDetailsId: params.row.itemDetaildId,
                  },
                });
                setAddModal({
                  operation: Operation.UPDATE,
                  flag: true,
                });
              }}
            />
          </>
        );
      },
    },
  ];

  const rows1: GridValidRowModel[] = useMemo(() => {
    return items.map(({ node: data }, index) => {
      return {
        id: index + 1,
        prodSerialNo: data.prodSerialNo,
        code: data.item.code,
        poleNum: data.poleNum,
        description: data.item.desc,
        prodBrand: data.prodBrand,
        prodWarrantyStartDate:
          toStandardDate(data.prodWarrantyStartDate) !==
          toStandardDate(DEFAULT_TIME)
            ? toStandardDate(data.prodWarrantyStartDate)
            : "-",
        prodWarrantyEndDate:
          toStandardDate(data.prodWarrantyEndDate) !==
          toStandardDate(DEFAULT_TIME)
            ? toStandardDate(data.prodWarrantyEndDate)
            : "-",
        prodAmcStDate:
          toStandardDate(data.prodAmcStDate) !== toStandardDate(DEFAULT_TIME)
            ? toStandardDate(data.prodAmcStDate)
            : "-",
        prodAmcEndDate:
          toStandardDate(data.prodAmcEndDate) !== toStandardDate(DEFAULT_TIME)
            ? toStandardDate(data.prodAmcEndDate)
            : "-",
        prodInsuranceStDate:
          toStandardDate(data.prodInsuranceStDate) !==
          toStandardDate(DEFAULT_TIME)
            ? toStandardDate(data.prodInsuranceStDate)
            : "-",
        prodInsuranceEndDate:
          toStandardDate(data.prodInsuranceEndDate) !==
          toStandardDate(DEFAULT_TIME)
            ? toStandardDate(data.prodInsuranceEndDate)
            : "-",
        geoLocation: `${data.prodLatitude}, ${data.prodLongitude}`,
        address: data.address,
        group: data.item.groupDetails.groupName,
        category: data.item.categoryDetails.categoryName,
        sub_category: data.item.subCategoryDetails.subCategoryName,
        model: data.prodModel,
        uom: data.item.uomDetails.uomDesc,
        itemDetaildId: data.id,
      }; 
    });
  }, [items]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.ITEMS_LIST);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.ITEMS_LIST,
      JSON.stringify(newModel)
    );
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemsDetails.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,

              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
      } else {
        setItems(newData);
      }
      setEndCursor(data.GetItemsDetails.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetItemsDetails.edges;
              const pageInfo = fetchMoreResult.GetItemsDetails.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetItemsDetails.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetItemsDetails: {
                  edges: [...items, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetItemsDetails.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable) scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [items, data]);

  const [getSubOrSiteOrJunctionById, { data: SubOrSiteOrJunctionData }] =
    useLazyQuery<{ node: getSubOrSiteOrJunctionByIdResult }, nodeVar>(
      GetSubOrSiteOrJunctionById
    );
  useEffect(() => {
    const fetchSubsidiaryDetails = async () => {
      try {
        if (sub_id || siteID || junctionID) {
          getSubOrSiteOrJunctionById({
            variables: {
              id: junctionID
                ? junctionID
                : siteID
                ? siteID
                : sub_id
                ? sub_id
                : 0,
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchSubsidiaryDetails();
  }, [sub_id, siteID, junctionID, getSubOrSiteOrJunctionById]);
  const { itemLabel } = useLabels();

  return (
    <>
      {pageType === PageFor.GENERAL && <Navbar NavType={NavType} />}
      {pageType === PageFor.GENERAL ? (
        <Title>
          {SubOrSiteOrJunctionData
            ? SubOrSiteOrJunctionData.node.name
            : EMPTY_STRING}{" "}
          Items List
        </Title>
      ) : (
        <div className="item-ob__title">
          <Title>
            {SubOrSiteOrJunctionData
              ? SubOrSiteOrJunctionData.node.name
              : EMPTY_STRING}{" "}
            Items List
          </Title>
          <img src={Close} alt="" onClick={() => setModalFlag(false)} />
        </div>
      )}

      <div
        className={`item-ob${pageType === PageFor.GENERAL ? "" : "__modal"}`}
      >
        <div className="item-ob__select row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchJunction(e.target.value);
              }}
            />
          </div>
          <div className="col-2">
            <ItemGroupSelect
              onChange={(newValue) => {
                if (newValue) {
                  setFilterData({
                    ...filterData,
                    ItemGroupId: newValue.value,
                  });
                } else {
                  setFilterData({
                    ...filterData,
                    ItemGroupId: 0,
                  });
                }
              }}
              value={filterData.ItemGroupId}
              onKeyDown={handleMUISelectEvent}
              label={`${itemLabel} Group`}
            />
          </div>
          <div className="col-2">
            <ItemCategorySelect
              onChange={(newValue) => {
                if (newValue) {
                  setFilterData({
                    ...filterData,
                    ItemCategoryId: newValue.value,
                  });
                } else {
                  setFilterData({
                    ...filterData,
                    ItemCategoryId: 0,
                  });
                }
              }}
              value={filterData.ItemCategoryId}
              label={`${itemLabel} Category`}
              onKeyDown={handleMUISelectEvent}
            />
          </div>
          <div className="col-4">
            <PoleNumComponent
              onChange={(newValue) => {
                setPoleNum(newValue);
              }}
              onKeyDown={emptyFunc}
              value={poleNum}
              label="Pole Number"
              poleNum={junctionData ? junctionData.node.poles : 0}
            />
          </div>
          {/* <div className="col">
                <LabeledAutocomplete
              className={labelClasses.inputRoot}
              onKeyDown={(e) => {
                if (e.key === Keys.ENTER) {
                  e.preventDefault();
                  handleMUISelectEvent(e);
                }
              }}
              options={groupOptions}
              openOnFocus
              forcePopupIcon
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  className={labelClasses.formControlRoot}
                  label="Sub Category"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
            />
          </div>
          <div className="col">
            <LabeledAutocomplete
              className={labelClasses.inputRoot}
              onKeyDown={(e) => {
                if (e.key === Keys.ENTER) {
                  e.preventDefault();
                  handleMUISelectEvent(e);
                }
              }}
              options={groupOptions}
              openOnFocus
              forcePopupIcon
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  className={labelClasses.formControlRoot}
                  label="Manufacturer"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
            />
          </div> */}
        </div>
        <div className="item-ob__tableblock">
          {items.length ? (
            <StyledDatagrid
              columns={columns}
              rows={rows1}
              rowHeight={TABLE_ROW_HEIGHT}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              initialState={{
                columns: {
                  columnVisibilityModel: columnVisibilityModel,
                },
              }}
              disableRowSelectionOnClick
              hideFooter
            />
          ) : (
            <>
              <img src={NoData} alt="" className="nodata-image" />
            </>
          )}
        </div>
        <div className="junctions__button--group">
          <div className="col">
            {pageType === PageFor.GENERAL ? (
              <Button
                mode="excel"
                type="button"
                onClick={() => setImportModal(true)}
              >
                Import {itemLabel}
              </Button>
            ) : null}
            {pageType === PageFor.GENERAL ? (
              <Button mode="back" onClick={() => navigate(-1)} />
            ) : (
              <Button mode="cancel" onClick={() => setModalFlag(false)} />
            )}
          </div>
          <div className="overall-total-count">
            Total count : <b>{data?.GetItemsDetails.totalCount}</b>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={importModal}
        onRequestClose={() => setImportModal(!importModal)}
        contentComponent={
          <ImportItemsDetails
            setModalFlag={setImportModal}
            NavType={LicenseTypes.ADMIN}
            pageType={PageFor.MODAL}
            bulkAlterPage={false}
          />
        }
        style={AddModalStyles}
      />
      <Modal isOpen={addModal.flag} style={AddModalStyles} ariaHideApp={false}>
        <AddItemDetails
          setModalFlag={setAddModal}
          operation={addModal.operation}
        />
      </Modal>
    </>
  );
};

export default JunctionsItemList;
